/**
 * JWT utility functions for token management
 * Note: These functions decode tokens without verification (for expiry checking only)
 */

interface DecodedToken {
  exp?: number;
  iat?: number;
  customerId?: number;
  email?: string;
  accountCode?: string;
  [key: string]: any;
}

/**
 * Decode JWT token without verification
 * Returns null if token is invalid
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
}

/**
 * Check if token is expired (actually expired, not just expiring soon)
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const now = Date.now();

  return expiryTime <= now;
}

/**
 * Check if token expires within the buffer time (for automatic refresh)
 */
export function isTokenExpiringSoon(token: string, bufferMinutes: number = 5): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const bufferTime = bufferMinutes * 60 * 1000; // Convert to milliseconds

  return expiryTime <= (now + bufferTime);
}

/**
 * Get time until token expires in milliseconds
 * Returns 0 if expired or invalid
 */
export function getTimeUntilExpiry(token: string): number {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return 0;
  }

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const timeUntilExpiry = expiryTime - now;

  return Math.max(0, timeUntilExpiry);
}

